import type { DocumentCompletion, PdfRevisionProgress, PublicEvent, PublishedEventRevision } from '../../shared/types';
import { todayIso } from './state';
import { pdfDocumentSignatureContext } from '../../shared/revisionRecovery';
import { resolvePdfBindings, validateFormAnswers } from '../../shared/pdfBindings';
import { samePdfSource } from '../../shared/pdfBindingValidation';
import { completionSignatures } from './pdfSignatures';
import type { RegState } from './state';
import { readPdfRevisionProgress } from '../../shared/pdfProgress';

export function emptyDocument(document: NonNullable<PublicEvent['documentForms']>[number]): DocumentCompletion {
  return { documentId: document.id, templateSha256: document.templateSha256, answers: {}, reviewed: false, confirmedSignatureRoles: [], signedDates: {} };
}
export function initializePdfProgress(event: PublicEvent, saved?: PdfRevisionProgress): PdfRevisionProgress {
  const previous = readPdfRevisionProgress(saved);
  const sameRevision = previous.publishedRevisionId === event.publishedRevisionId;
  const documents = Object.fromEntries((event.documentForms ?? []).map(document => {
    const old = sameRevision ? previous.documents[document.id] : undefined;
    return [document.id, old?.completion.templateSha256 === document.templateSha256 ? { ...old, completion: { ...old.completion, answers: Object.fromEntries(Object.entries(old.completion.answers).filter(([id]) => document.questions.some(q => q.id === id))) } } : { completion: emptyDocument(document) }];
  }));
  return { ...previous, publishedRevisionId: event.publishedRevisionId, documents,
    activeDocumentId: previous.activeDocumentId && Object.hasOwn(documents, previous.activeDocumentId) ? previous.activeDocumentId : event.documentForms?.[0]?.id };
}
/** A browser-local collecting snapshot; no public route is allowed to read superseded templates. */
export function participantRevision(event: PublicEvent): PublishedEventRevision {
  const { status: _status, pdfSubmissionEnabled: _capability, submissionProtocols: _protocols, ...configuration } = event;
  const documents = event.documentForms ?? [];
  return { schemaVersion: 1, id: event.publishedRevisionId ?? 'organizer-preview', eventId: event.eventId ?? event.path, publishedAt: '',
    event: configuration, documentForms: documents, templates: documents.map(d => ({ assetId: d.assetId, templateSha256: d.templateSha256 })) };
}

/** Consent changes must be explicit; ordinary answer edits always clear document consent. */
export function clearDocumentConsent(completion: DocumentCompletion): DocumentCompletion {
  const { participantSignature: _participant, parentSignature: _parent, ...rest } = completion;
  return { ...rest, reviewed: false, confirmedSignatureRoles: [], signedDates: {} };
}

export function invalidatePdfContexts(event: PublicEvent, state: RegState): RegState {
  if (!state.pdfProgress) return state;
  const revision = participantRevision(event);
  const documents = Object.fromEntries(Object.entries(state.pdfProgress.documents).map(([id, progress]) => {
    const context = pdfDocumentSignatureContext(revision, state.builtin, state.custom, progress.completion);
    return [id, progress.signatureContext && progress.signatureContext === context ? progress : { completion: clearDocumentConsent(progress.completion) }];
  }));
  return { ...state, pdfProgress: { ...state.pdfProgress, documents } };
}
export function documentReviewIssues(event: PublicEvent, document: NonNullable<PublicEvent['documentForms']>[number], state: Pick<RegState, 'builtin' | 'custom'>, completion: DocumentCompletion) {
  const resolved = resolvePdfBindings({ event, document, builtin: state.builtin, custom: state.custom, documentAnswers: completion.answers });
  const issues = [...resolved.issues, ...validateFormAnswers(document, completion.answers)];
  for (const source of document.requiredSources ?? []) {
    const placements = document.placements.filter(p => samePdfSource(source, p.source));
    if (!placements.length || placements.some(p => !resolved.values[p.id]?.available)) issues.push({ code: 'required_source_unavailable', placementId: placements[0]?.id, message: 'Complete this required original answer before reviewing the document.' });
  }
  return issues;
}
export function documentCompletionIssues(event: PublicEvent, document: NonNullable<PublicEvent['documentForms']>[number], state: RegState) {
  const progress = state.pdfProgress?.documents[document.id];
  if (!progress) return ['Complete this document.'];
  const c = progress.completion;
  const issues = resolvePdfBindings({ event, document, builtin: state.builtin, custom: state.custom, documentAnswers: c.answers, signatures: completionSignatures(c), submittedOn: state.standardSignedOn ?? todayIso() }, { requireComplete: true }).issues.map(i => i.message);
  if (!c.reviewed || !progress.signatureContext || progress.signatureContext !== pdfDocumentSignatureContext(participantRevision(event), state.builtin, state.custom, c)) issues.push('Sign this document.');
  return issues;
}
